import { KeenIcon } from '@/components';
import { useRef, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { formatDatePreview, formatTimePreview } from '@/hooks/DateTimePreview';
import { toAbsoluteUrl } from '@/utils/Assets';
import { urlSchema } from '@/utils';
import Cropper from 'react-easy-crop';
import Moveable from 'react-moveable';
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const DEFAULT_CROP_SIZE = { width: 260, height: 160 };
const MIN_CROP_SIZE = { width: 20, height: 20 };
const SMALL_IMAGE_MIN_ZOOM = 0.2;

const getInitialCropSize = (imageWidth, imageHeight) => {
  if (!imageWidth || !imageHeight) {
    return DEFAULT_CROP_SIZE;
  }

  const scale = Math.min(
    DEFAULT_CROP_SIZE.width / imageWidth,
    DEFAULT_CROP_SIZE.height / imageHeight,
    1,
  );

  const width = Math.max(
    MIN_CROP_SIZE.width,
    Math.min(DEFAULT_CROP_SIZE.width, Math.round(imageWidth * scale)),
  );
  const height = Math.max(
    MIN_CROP_SIZE.height,
    Math.min(DEFAULT_CROP_SIZE.height, Math.round(imageHeight * scale)),
  );

  return { width, height };
};

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });

const toPngFileName = (fileName = 'company-logo') => {
  const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
  return `${nameWithoutExt || 'company-logo'}.png`;
};

const getCroppedImageFile = async (imageSrc, pixelCrop, fileName) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(pixelCrop.width));
  canvas.height = Math.max(1, Math.round(pixelCrop.height));
  const ctx = canvas.getContext('2d');

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Unable to process image'));
        return;
      }
      resolve(new File([blob], toPngFileName(fileName), { type: 'image/png' }));
    }, 'image/png');
  });
};

const BasicSettings = ({ data, onConfirm, loadData, loadDataSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [pendingLogoName, setPendingLogoName] = useState('company-logo.jpg');
  const [cropSize, setCropSize] = useState(DEFAULT_CROP_SIZE);
  const cropBoxRef = useRef(null);
  const cropContainerRef = useRef(null);
  const resizeRafRef = useRef(null);

  const getLogoSrc = (value) => {
    if (Array.isArray(value) && value[0]?.dataURL) {
      return value[0].dataURL;
    }

    if (typeof value === 'string') {
      return value === 'no_image' ? toAbsoluteUrl('/media/avatars/blank.png') : value;
    }

    return toAbsoluteUrl('/media/avatars/blank.png');
  };

  const revokeLogoPreviewIfNeeded = (value) => {
    const maybeBlobUrl = Array.isArray(value) ? value[0]?.dataURL : '';
    if (typeof maybeBlobUrl === 'string' && maybeBlobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(maybeBlobUrl);
    }
  };

  const resetCropState = () => {
    if (resizeRafRef.current) {
      cancelAnimationFrame(resizeRafRef.current);
      resizeRafRef.current = null;
    }
    setCropImageSrc('');
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setMinZoom(1);
    setCroppedAreaPixels(null);
    setPendingLogoName('company-logo.jpg');
    setCropSize(DEFAULT_CROP_SIZE);
  };

  const handleCancel = (resetForm, values) => {
    revokeLogoPreviewIfNeeded(values.company_logo);
    setIsEditing(false);
    setIsCropOpen(false);
    resetCropState();
    resetForm();
  };

  const formFields = {
    company_logo: { label: 'Company Logo', type: 'file' },
    account_owner: { label: 'Account Owner' },
    legal_business_name: { label: 'Legal Business Name' },
    // country: { label: 'Country' },
    // address: { label: 'Address' },
    // address_2: { label: 'Address Line 2' },
    // city: { label: 'City' },
    // zip_postal_code: { label: 'Zip/Postal Code' },
    phone_number: { label: 'Phone Number' },
    // firm_email: { label: 'Firm Email' },
    website: { label: 'Website' },
    tax_id: { label: 'Tax ID' },
    // date_format: {
    //   label: 'Date Format',
    //   type: 'select',
    //   options: [
    //     { label: 'YYYY-MM-DD (2025-04-16)', value: 'Y-m-d' },
    //     { label: 'DD-MM-YYYY (16-04-2025)', value: 'd-m-Y' },
    //     { label: 'MM-DD-YYYY (04-16-2025)', value: 'm-d-Y' },
    //     { label: 'DD-MMM-YYYY (16-Apr-2025)', value: 'd-M-Y' },
    //     { label: 'D-M-YYYY (16-4-2025)', value: 'j-n-Y' },
    //     { label: 'YYYY/MM/DD (2025/04/16)', value: 'Y/m/d' },
    //     { label: 'DD/MM/YYYY (16/04/2025)', value: 'd/m/Y' },
    //     { label: 'MM/DD/YYYY (04/16/2025)', value: 'm/d/Y' },
    //     { label: 'DD/MMM/YYYY (16/Apr/2025)', value: 'd/M/Y' },
    //     { label: 'D/M/YYYY (16/4/2025)', value: 'j/n/Y' },
    //   ],
    // },
    // time_format: {
    //   label: 'Time Format',
    //   type: 'custom-time-format', // we'll handle this manually in the JSX
    // },
  };

  const validationSchema = Yup.object().shape({
    account_owner: Yup.string().required('Account Owner is required'),
    legal_business_name: Yup.string().required('Business Name is required'),
    // country: Yup.string().required('Country is required'),
    // address: Yup.string().required('Address is required'),
    // city: Yup.string().required('City is required'),
    // zip_postal_code: Yup.string().required('Zip Code is required'),
    phone_number: Yup.string()
      .required('Phone Number is required')
      .matches(
        /^(\+?\d{1,4}[\s-]?)?(\(?\d{3}\)?[\s.-]?)?[\d\s.-]{5,}$/,
        'Invalid phone number'
      ),
    // firm_email: Yup.string().email('Invalid email').required('Email is required'),
    website: urlSchema('Invalid URL'),
    tax_id: Yup.string(),
    // date_format: Yup.string(),
    // time_format: Yup.string(),
  });

  const initialValues = {
    account_owner: data?.account_owner || '',
    legal_business_name: data?.legal_business_name || '',
    country: data?.country || '',
    address: data?.address || '',
    address_2: data?.address_2 || '',
    city: data?.city || '',
    zip_postal_code: data?.zip_postal_code || '',
    phone_number: data?.phone_number || '',
    firm_email: data?.firm_email || '',
    website: data?.website || '',
    tax_id: data?.tax_id || '',
    date_format: data?.date_format || '',
    time_format: data?.time_format || '',
    // company_logo: data?.company_logo ? [{ dataURL: data?.company_logo }] : []
    company_logo: data?.company_logo ? data?.company_logo : [],
  };

  return (
    <div className="card pb-2.5">

      <div className="card-header" id="basic_settings">
        <h3 className="card-title">Basic Settings</h3>
        <button onClick={() => setIsEditing(true)} className="btn btn-xs btn-icon btn-icon-lg link">
          <KeenIcon icon="notepad-edit" className="text-[12px]" />
        </button>
      </div>
      <div className="card-body grid gap-5">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={(values) => {
            const formData = new FormData();
            const avatarFile = Array.isArray(values.company_logo) && values.company_logo.length > 0
              ? values.company_logo[0].file
              : values.company_logo;
            const finalData = {
              ...initialValues,   // pre-fill everything
              ...values,        // override only what was edited in the form
              company_logo: avatarFile,
            };
            Object.entries(finalData).forEach(([key, value]) => {
              formData.append(key, value ?? '');
            });
            formData.append('_method', 'PUT');
            onConfirm(formData);
            setIsEditing(false);
          }}
        >
          {({ values, setFieldValue, resetForm }) => (
            <Form>
              <table className="table align-middle text-sm text-gray-500">
                <tbody>
                  {loadData || loadDataSave ? (
                    <tr>
                      <td colSpan={2} className="text-center py-4">
                        <span>Loading...</span>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {/* <tr>
                          <td className="text-gray-800 font-medium">Firm Info</td>
                          <td></td>
                        </tr> */}

                      {Object.entries(formFields).map(([key, config]) => (
                        <tr key={key}>
                          <td className="py-2 text-gray-600">{config.label}</td>
                          <td className="py-2 text-gray-800">
                            {isEditing ? (
                              key === 'time_format' ? (
                                <div>
                                  <div className="flex gap-4 mb-2">
                                    <label className="flex items-center gap-1">
                                      <Field
                                        type="radio"
                                        name="time_format_group"
                                        value="24"
                                        checked={['H:i', 'H:i:s', 'H.i', 'H.i.s'].includes(values.time_format)}
                                        onChange={() => {
                                          setFieldValue('time_format', 'H:i');
                                        }}
                                      />
                                      24-Hour
                                    </label>
                                    <label className="flex items-center gap-1">
                                      <Field
                                        type="radio"
                                        name="time_format_group"
                                        value="12"
                                        checked={['h:i A', 'h:i:s A', 'h:i a', 'h:i:s a'].includes(values.time_format)}
                                        onChange={() => {
                                          setFieldValue('time_format', 'h:i A');
                                        }}
                                      />
                                      12-Hour
                                    </label>
                                  </div>
                                  <Field as="select" name="time_format" className="input input-sm input-bordered w-full">
                                    {['H:i', 'H:i:s', 'H.i', 'H.i.s'].includes(values.time_format) && (
                                      <>
                                        <option value="H:i">24-Hour: HH:MM (14:30)</option>
                                        <option value="H:i:s">24-Hour: HH:MM:SS (14:30:00)</option>
                                        <option value="H.i">24-Hour: HH.MM (14.30)</option>
                                        <option value="H.i.s">24-Hour: HH.MM.SS (14.30.00)</option>
                                      </>
                                    )}
                                    {['h:i A', 'h:i:s A', 'h:i a', 'h:i:s a'].includes(values.time_format) && (
                                      <>
                                        <option value="h:i A">12-Hour: HH:MM AM/PM (02:30 PM)</option>
                                        <option value="h:i:s A">12-Hour: HH:MM:SS AM/PM (02:30:00 PM)</option>
                                        <option value="h:i a">12-Hour: HH:MM am/pm (02:30 pm)</option>
                                        <option value="h:i:s a">12-Hour: HH:MM:SS am/pm (02:30:00 pm)</option>
                                      </>
                                    )}
                                  </Field>
                                </div>
                              ) : key === 'date_format' ? (
                                <Field as="select" name="date_format" className="input input-sm input-bordered w-full">
                                  {config.options.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </Field>
                              ) : key === 'company_logo' ? (
                                <>
                                  <div className="flex items-center gap-4">
                                    <div className="size-20 rounded-full border border-gray-300 overflow-hidden bg-gray-50">
                                      <img
                                        src={getLogoSrc(values.company_logo)}
                                        alt="Company Logo"
                                        className="size-full object-cover"
                                      />
                                    </div>

                                    <div className="flex flex-col items-start gap-2">
                                      <label className="btn btn-sm btn-light cursor-pointer">
                                        Upload & Resize
                                        <input
                                          type="file"
                                          accept="image/png,image/jpeg,image/jpg,image/webp"
                                          className="hidden"
                                          onChange={(event) => {
                                            const selectedFile = event.target.files?.[0];
                                            event.target.value = '';

                                            if (!selectedFile) {
                                              return;
                                            }

                                            const fileReader = new FileReader();
                                            fileReader.onload = async () => {
                                              const uploadedImageSrc = String(fileReader.result);
                                              const image = await createImage(uploadedImageSrc);
                                              const isSmallImage =
                                                image.width < DEFAULT_CROP_SIZE.width || image.height < DEFAULT_CROP_SIZE.height;

                                              setPendingLogoName(selectedFile.name || 'company-logo.jpg');
                                              setCropImageSrc(uploadedImageSrc);
                                              setCrop({ x: 0, y: 0 });
                                              setZoom(1);
                                              setMinZoom(isSmallImage ? SMALL_IMAGE_MIN_ZOOM : 1);
                                              setCroppedAreaPixels(null);
                                              setCropSize(getInitialCropSize(image.width, image.height));
                                              setIsCropOpen(true);
                                            };
                                            fileReader.readAsDataURL(selectedFile);
                                          }}
                                        />
                                      </label>

                                      <button
                                        type="button"
                                        className="btn btn-sm btn-light-danger"
                                        onClick={() => {
                                          revokeLogoPreviewIfNeeded(values.company_logo);
                                          setFieldValue('company_logo', 'no_image');
                                        }}
                                      >
                                        Remove
                                      </button>

                                      <span className="block text-xs">Recommended: 15:5 ratio (180x50)</span>
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <Field name={key} className="input input-sm input-bordered w-full" />
                                  <ErrorMessage name={key} component="div" className="text-red-500 text-xs mt-1" />
                                </>
                              )
                            ) : (
                              key === 'date_format'
                                ? formatDatePreview(values[key])
                                : key === 'time_format'
                                  ? formatTimePreview(values[key])
                                  : key === 'website'
                                    ? <a href={values[key]} target="_blank" rel="noopener noreferrer" className="link">
                                      {values[key]}
                                    </a>
                                    : key === 'firm_email'
                                      ? <a href={`mailto:${values[key]}`} rel="noopener noreferrer" className="">
                                        {values[key]}
                                      </a>
                                      : key === 'phone_number'
                                        ? <a href={`tel:${values[key]}`} rel="noopener noreferrer" className="">
                                          {values[key]}
                                        </a>
                                        : key === 'company_logo'
                                          ? <img src={getLogoSrc(values[key])} alt="Company Logo" className="w-20 h-20 rounded-full object-contain" />
                                          : values[key] || '-'
                            )}
                          </td>
                        </tr>
                      ))}
                      {isEditing && (
                        <tr>
                          <td></td>
                          <td className="pt-3">
                            <button
                              type="submit"
                              disabled={loadDataSave}
                              className="btn btn-sm btn-primary"
                            >
                              {loadDataSave ? "Saving..." : "Save"}
                            </button>
                            <button
                              type="button"
                              onClick={() => { handleCancel(resetForm, values); }}
                              className="btn btn-sm "
                            >
                              Cancel
                            </button>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>

              <Dialog
                open={isCropOpen}
                onOpenChange={(open) => {
                  setIsCropOpen(open);
                  if (!open) {
                    resetCropState();
                  }
                }}
              >
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Resize Company Logo</DialogTitle>
                  </DialogHeader>

                  <DialogBody className="space-y-4 pb-2">
                    <div className="relative h-[320px] w-full overflow-hidden rounded-md bg-gray-100">
                      {cropImageSrc && (
                        <div ref={cropContainerRef} className="absolute inset-0">
                          <Cropper
                            image={cropImageSrc}
                            crop={crop}
                            zoom={zoom}
                            minZoom={minZoom}
                            cropSize={cropSize}
                            aspect={cropSize.width / cropSize.height}
                            cropShape="rect"
                            showGrid
                            objectFit="contain"
                            restrictPosition={false}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
                          />

                          <div
                            ref={cropBoxRef}
                            className="pointer-events-none absolute left-1/2 top-1/2 border-2 border-white"
                            style={{
                              width: `${cropSize.width}px`,
                              height: `${cropSize.height}px`,
                              transform: 'translate(-50%, -50%)',
                            }}
                          />

                          {isCropOpen && (
                            <Moveable
                              target={cropBoxRef.current}
                              resizable
                              keepRatio={false}
                              edge
                              draggable={false}
                              origin={false}
                              throttleResize={1}
                              renderDirections={['n', 's', 'e', 'w', 'nw', 'ne', 'sw', 'se']}
                              onResizeStart={({ set }) => {
                                set([cropSize.width, cropSize.height]);
                              }}
                              onResize={({ target, width, height }) => {
                                const containerRect = cropContainerRef.current?.getBoundingClientRect();
                                const maxWidth = Math.max(MIN_CROP_SIZE.width, Math.floor(containerRect?.width || DEFAULT_CROP_SIZE.width));
                                const maxHeight = Math.max(MIN_CROP_SIZE.height, Math.floor(containerRect?.height || DEFAULT_CROP_SIZE.height));
                                const nextWidth = Math.min(maxWidth, Math.max(MIN_CROP_SIZE.width, Math.round(width)));
                                const nextHeight = Math.min(maxHeight, Math.max(MIN_CROP_SIZE.height, Math.round(height)));

                                target.style.width = `${nextWidth}px`;
                                target.style.height = `${nextHeight}px`;

                                if (resizeRafRef.current) {
                                  cancelAnimationFrame(resizeRafRef.current);
                                }

                                resizeRafRef.current = requestAnimationFrame(() => {
                                  setCropSize({ width: nextWidth, height: nextHeight });
                                });
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="mb-2 text-sm text-gray-700">Zoom: {zoom.toFixed(1)}x</div>
                      <Slider
                        min={minZoom}
                        max={3}
                        step={0.1}
                        value={[zoom]}
                        onValueChange={(nextValue) => setZoom(nextValue[0] ?? minZoom)}
                      />
                    </div>
                  </DialogBody>

                  <DialogFooter className="gap-2 px-5 pb-5">
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => {
                        setIsCropOpen(false);
                        resetCropState();
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={async () => {
                        if (!cropImageSrc || !croppedAreaPixels) {
                          return;
                        }

                        const croppedFile = await getCroppedImageFile(
                          cropImageSrc,
                          croppedAreaPixels,
                          pendingLogoName,
                        );
                        const previewUrl = URL.createObjectURL(croppedFile);

                        revokeLogoPreviewIfNeeded(values.company_logo);
                        setFieldValue('company_logo', [{
                          file: croppedFile,
                          dataURL: previewUrl,
                        }]);

                        setIsCropOpen(false);
                        resetCropState();
                      }}
                    >
                      Apply
                    </button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};
export { BasicSettings };
